import { useQuery } from '@apollo/client';
import classNames from 'classnames/bind';
import { LocationType, TLocation } from 'common/autocomplete/types';
import { debounce } from 'common/utils';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import SearchEverywhere from '../popularCities/SearchEverywhere';
import Location from './Location';
import SEARCH_AIRPORTS from './airportAutocomplete.gql';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

interface IData {
  airportAutocomplete: TLocation[];
}

interface IProps {
  forDeparture: boolean;
  query: string;
  closeModal: () => void;
}

interface SearchResultItem {
  airportName: string | null;
  cityName: string | null;
  countryName: string;
  iataCode: string | null;
  type: LocationType;
}

const transformSearchResults = (locations: TLocation[]): SearchResultItem[] =>
  locations.map(location => ({
    airportName: 'airportName' in location ? location.airportName : null,
    cityName: 'cityName' in location ? location.cityName : null,
    countryName: location.countryName,
    iataCode: 'iataCode' in location ? location.iataCode : null,
    type: location.type,
  }));

// 사용자 검색 기록 nLog를 사용하여 로깅
const logQuery = debounce(
  ({
    pathname,
    query,
    searchResult,
  }: {
    pathname: string;
    query: string;
    searchResult: SearchResultItem[];
  }) => {
    window?.ntm?.push({
      event: 'searchQuery',
      nlogEvt: {
        pathname,
        query,
        flight: {
          searchResult,
        },
      },
    });
  },
  1000,
);

const SearchResults = ({ forDeparture, query }: IProps) => {
  const router = useRouter();
  const { data, previousData } = useQuery<IData>(SEARCH_AIRPORTS, {
    variables: { airportOnly: false, query },
  });

  const cachedData = data || previousData;
  const locations = useMemo(
    () =>
      cachedData?.airportAutocomplete.filter(
        location => !(forDeparture && location.type === LocationType.COUNTRY),
      ) ?? [],
    [cachedData, forDeparture],
  );

  useEffect(() => {
    if (query) {
      logQuery({
        pathname: router.pathname,
        query,
        searchResult: transformSearchResults(locations),
      });
    }
  }, [locations, query, router.pathname]);

  return (
    <section className={cx('SearchResults')}>
      {locations.length > 0 ? (
        <ul className={cx('result_list')}>
          <Location
            forDeparture={forDeparture}
            locations={locations}
            query={query}
          />
        </ul>
      ) : (
        <div className={cx('error')}>
          <div className={cx('noresult')}>
            <div className={cx('inner')}>
              <p className={cx('text')}>
                입력하신 명칭과 연관된
                <br />
                국가, 도시, 공항명이 없습니다.
              </p>
            </div>
          </div>
          {!forDeparture && (
            <div className={cx('sub')}>
              <SearchEverywhere isNoResult />
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default SearchResults;
